import { useEffect, useRef, useCallback, useState } from 'react';

/**
 * Custom hook for drag-to-resize functionality on mobile sidebars
 * @param {boolean} isMobile - Whether the device is mobile
 * @param {string} initialHeight - Initial height of the sidebar (e.g., "60vh")
 * @param {number} minHeightVh - Minimum height in vh units (default: 20)
 * @param {number} maxHeightVh - Maximum height in vh units (default: 90)
 * @param {Function} onHeightChange - Optional callback when height changes
 * @returns {Object} - { height, dragHandleProps }
 */
export const useMobileDragResize = (isMobile, initialHeight = "60vh", minHeightVh = 20, maxHeightVh = 90, onHeightChange = null) => {
  const [height, setHeight] = useState(initialHeight);
  const isDraggingRef = useRef(false);
  const hasDraggedRef = useRef(false);
  const startYRef = useRef(0);
  const startHeightRef = useRef(0);

  // Convert vh string to number
  const parseVh = (vhString) => {
    const match = vhString.match(/(\d+\.?\d*)vh/);
    return match ? parseFloat(match[1]) : 60;
  };

  // Update height when initialHeight changes
  useEffect(() => {
    setHeight(initialHeight);
    if (isMobile && onHeightChange) {
      onHeightChange(initialHeight);
    }
  }, [initialHeight, isMobile, onHeightChange]);

  const handleTouchStart = useCallback((e) => {
    if (!isMobile) return;

    isDraggingRef.current = true;
    hasDraggedRef.current = false;
    startYRef.current = e.touches[0].clientY;
    startHeightRef.current = parseVh(height);

    // Prevent default to avoid scrolling while dragging
    e.preventDefault();
  }, [isMobile, height, parseVh]);

  const handleTouchMove = useCallback((e) => {
    if (!isMobile || !isDraggingRef.current) return;

    const currentY = e.touches[0].clientY;
    const deltaY = startYRef.current - currentY; // Inverted: dragging up increases height

    // Mark as dragged if moved more than 5 pixels
    if (Math.abs(deltaY) > 5) {
      hasDraggedRef.current = true;
    }

    const viewportHeight = window.innerHeight;
    const deltaVh = (deltaY / viewportHeight) * 100;

    let newHeightVh = startHeightRef.current + deltaVh;

    // Clamp between min and max
    newHeightVh = Math.max(minHeightVh, Math.min(maxHeightVh, newHeightVh));

    const newHeight = `${newHeightVh}vh`;
    setHeight(newHeight);

    // Notify parent component of height change
    if (onHeightChange) {
      onHeightChange(newHeight);
    }

    e.preventDefault();
  }, [isMobile, minHeightVh, maxHeightVh, onHeightChange]);

  const handleTouchEnd = useCallback(() => {
    if (!isMobile) return;
    isDraggingRef.current = false;
  }, [isMobile]);

  const handleMouseDown = useCallback((e) => {
    if (!isMobile) return;

    isDraggingRef.current = true;
    hasDraggedRef.current = false;
    startYRef.current = e.clientY;
    startHeightRef.current = parseVh(height);

    e.preventDefault();
  }, [isMobile, height, parseVh]);

  const handleMouseMove = useCallback((e) => {
    if (!isMobile || !isDraggingRef.current) return;

    const currentY = e.clientY;
    const deltaY = startYRef.current - currentY; // Inverted: dragging up increases height

    // Mark as dragged if moved more than 5 pixels
    if (Math.abs(deltaY) > 5) {
      hasDraggedRef.current = true;
    }

    const viewportHeight = window.innerHeight;
    const deltaVh = (deltaY / viewportHeight) * 100;

    let newHeightVh = startHeightRef.current + deltaVh;

    // Clamp between min and max
    newHeightVh = Math.max(minHeightVh, Math.min(maxHeightVh, newHeightVh));

    const newHeight = `${newHeightVh}vh`;
    setHeight(newHeight);

    // Notify parent component of height change
    if (onHeightChange) {
      onHeightChange(newHeight);
    }

    e.preventDefault();
  }, [isMobile, minHeightVh, maxHeightVh, onHeightChange]);

  const handleMouseUp = useCallback(() => {
    if (!isMobile) return;
    isDraggingRef.current = false;
  }, [isMobile]);

  // Handler to prevent click event after drag
  const handleClick = useCallback((e) => {
    if (!isMobile) return;

    // If user dragged, prevent the click from firing
    if (hasDraggedRef.current) {
      e.preventDefault();
      e.stopPropagation();
      hasDraggedRef.current = false; // Reset for next interaction
      return;
    }
  }, [isMobile]);

  // Set up global event listeners for mouse events (to handle dragging outside the button)
  useEffect(() => {
    if (!isMobile) return;

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isMobile, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  // Props to spread on the drag handle (close button)
  const dragHandleProps = {
    onTouchStart: handleTouchStart,
    onMouseDown: handleMouseDown,
    onClick: handleClick,
    style: {
      cursor: isMobile ? 'ns-resize' : 'pointer',
      touchAction: 'none',
      userSelect: 'none',
    }
  };

  return {
    height,
    dragHandleProps,
    // Expose method to get current height
    getCurrentHeight: () => height,
  };
};
